import React, { useState, useEffect } from 'react';
import { Typography } from 'antd';
import AnimatedText from './Text/AnimatedText';

const LoadingAnimation = ({
  title = 'Processing Data',
  words = [],
  seconds = 5,
  startProgressValue = 0,
  endProgressValue = 100,
  onFinish = () => {},  // Add onFinish callback prop
}) => {
  const [progress, setProgress] = useState(startProgressValue);
  const [displayedWords, setDisplayedWords] = useState([]);
  const [isProgressComplete, setIsProgressComplete] = useState(false);
  const [isWordsComplete, setIsWordsComplete] = useState(false);

  useEffect(() => {
    const wordInterval = (seconds * 1000) / words.length;
    const progressInterval = 16;
    const progressStep = (endProgressValue - startProgressValue) / (seconds * 1000 / progressInterval);

    const progressTimer = setInterval(() => {
      setProgress(prev => {
        const next = prev + progressStep;
        if (next >= endProgressValue) {
          clearInterval(progressTimer);
          setIsProgressComplete(true);
          return endProgressValue;
        }
        return next;
      });
    }, progressInterval);

    const wordTimer = setInterval(() => {
      setDisplayedWords(prev => {
        if (prev.length >= words.length) {
          clearInterval(wordTimer);
          setIsWordsComplete(true);
          return prev;
        }
        return [...prev, words[prev.length]];
      });
    }, wordInterval);

    return () => {
      clearInterval(progressTimer);
      clearInterval(wordTimer);
    };
  }, [words, seconds, startProgressValue, endProgressValue]);

  // Effect to handle completion
  useEffect(() => {
    if (isProgressComplete && isWordsComplete) {
      // Add a small delay to ensure animations are complete
      const finishTimeout = setTimeout(() => {
        onFinish();
      }, 500);

      return () => clearTimeout(finishTimeout);
    }
  }, [isProgressComplete, isWordsComplete, onFinish]);

  return (
    <div className="w-full min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-3/5 max-w-3xl">
        {/* Title */}
        <div className="mb-12">
          <AnimatedText text={title} />
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden shadow-inner">
            <div
              className="h-full transition-all duration-300 ease-out relative bg-gradient-to-r from-emerald-300 to-blue-500"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
            </div>
          </div>
          <div className="text-center mt-2 font-mono text-gray-600">
            {Math.round(progress)}%
          </div>
        </div>

        {/* Words Display */}
        <div className="flex flex-wrap gap-4 justify-center content-start p-4">
          {displayedWords.map((word, index) => (
            <div
              key={index}
              className="px-4 py-2 bg-gradient-to-br from-teal-50 to-emerald-50
                         rounded-lg border border-emerald-100 shadow-sm
                         animate-fade-in"
              style={{
                animation: 'fadeIn 0.5s ease-out forwards',
              }}
            >
              <span className="text-emerald-700 font-medium">
                {word}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingAnimation;